import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Logo from './Logo';
import { Button } from './ui/button';

const Navbar = ({ onOpenModal }) => {
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'backdrop-blur-2xl bg-black/60 border-b border-white/10' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div onClick={() => scrollToSection('hero')}>
            <Logo size="default" />
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8" data-testid="nav-links">
            {[
              { id: 'architecture', label: t('nav.architecture') },
              { id: 'predictive', label: t('nav.predictive') },
              { id: 'ecosystem', label: t('nav.ecosystem') }
            ].map((item) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium text-gray-400 hover:text-[#00F0FF] transition-colors duration-300 tracking-wide"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.label}
              </motion.button>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <motion.button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 hover:border-[#00F0FF]/50 transition-all duration-300 bg-black/40"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-xs font-mono text-white tracking-wider">
                {language.toUpperCase()}
              </span>
            </motion.button>

            {/* CTA Button */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={onOpenModal}
                className="hidden sm:flex items-center gap-2 px-6 py-2.5 bg-black/40 backdrop-blur-xl border border-[#00F0FF]/30 hover:border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-all duration-300 rounded-sm font-medium text-sm tracking-wide"
                data-testid="nav-cta-button"
              >
                {t('nav.requestAccess')}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;
